import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Typography,
  Divider,
  Paper,
  Stack,
  Grid,
  Chip,
  Button,
} from "@mui/material";
import EqualizerRoundedIcon from "@mui/icons-material/EqualizerRounded";
import GraphicEqRoundedIcon from "@mui/icons-material/GraphicEqRounded";
import TipsAndUpdatesRoundedIcon from "@mui/icons-material/TipsAndUpdatesRounded";
import AutoAwesomeRoundedIcon from "@mui/icons-material/AutoAwesomeRounded";
import TuneRoundedIcon from "@mui/icons-material/TuneRounded";
import CompareArrowsRoundedIcon from "@mui/icons-material/CompareArrowsRounded";
import LibraryMusicRoundedIcon from "@mui/icons-material/LibraryMusicRounded";
import MusicNoteRoundedIcon from "@mui/icons-material/MusicNoteRounded";
import { Link as RouterLink } from "react-router-dom";
import { useStyle } from "../../context/styleContext/styleContext";
import Seo from "../../components/Seo";

export default function TremoloLearning() {
  const { style } = useStyle();
  const [height, setHeight] = useState(window.innerHeight);
  const topContainer = useRef(null);

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
    topContainer.current?.scrollIntoView({ block: "start" });
  }, []);

  useEffect(() => {
    const handleResize = () => setHeight(window.innerHeight);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const accent = "rgb(88, 166, 124)";
  const panelBg = "rgba(255,255,255,0.04)";
  const darkPanel = "rgba(0,0,0,0.18)";

  const articleSchema = useMemo(
    () => ({
      "@context": "https://schema.org",
      "@type": "Article",
      headline: "Tremolo in Music Production: What It Is and How It Works",
      description:
        "Learn what tremolo does, how it modulates volume over time, how it differs from vibrato, and where tremolo is used in music production and guitar tones.",
      author: { "@type": "Organization", name: "Parish Audio" },
      publisher: {
        "@type": "Organization",
        name: "Parish Audio",
        logo: {
          "@type": "ImageObject",
          url: "https://parishaudio.com/assets/logo.png",
        },
      },
      mainEntityOfPage: "https://parishaudio.com/learning/tremolo",
      articleSection: "Audio Education",
      keywords: [
        "tremolo",
        "what does tremolo do",
        "tremolo effect",
        "tremolo vs vibrato",
        "guitar tremolo",
        "audio modulation",
        "volume modulation",
      ],
    }),
    []
  );

  const quickFacts = [
    {
      title: "What it does",
      text: "Tremolo changes volume over time, creating pulsing, throbbing, choppy, or rhythmic movement without changing the pitch.",
      icon: <EqualizerRoundedIcon sx={{ color: accent }} />,
    },
    {
      title: "Why it matters",
      text: "Tremolo adds motion and emotion with a very direct sound, from subtle shimmer to bold rhythmic chopping.",
      icon: <AutoAwesomeRoundedIcon sx={{ color: accent }} />,
    },
    {
      title: "Where it is used",
      text: "Electric guitar, keyboards, pads, vocals, ambient textures, and creative rhythmic production.",
      icon: <GraphicEqRoundedIcon sx={{ color: accent }} />,
    },
  ];

  const tremoloTypes = [
    {
      name: "Classic Amp Tremolo",
      description:
        "The vintage pulsing sound built into many classic amplifiers, often warm, musical, and strongly tied to guitar history.",
    },
    {
      name: "Optical Tremolo",
      description:
        "Known for a smooth and musical feel, often associated with classic amp circuits and rounded movement.",
    },
    {
      name: "Bias Tremolo",
      description:
        "A style of amp tremolo with a distinctive feel and a more organic pulsing quality in many vintage designs.",
    },
    {
      name: "Hard Chopper Tremolo",
      description:
        "Uses sharper waveform shapes for more dramatic rhythmic chopping and stutter-like effects.",
    },
    {
      name: "Stereo Tremolo / Auto-Pan",
      description:
        "Moves level or emphasis between left and right channels, creating width and motion across the stereo field.",
    },
    {
      name: "Tempo-Synced Tremolo",
      description:
        "Locks the tremolo speed to musical note divisions so the pulsing lines up with the song tempo.",
    },
  ];

  const tremoloConcepts = [
    {
      name: "Volume Modulation",
      description:
        "Tremolo works by modulating amplitude, meaning it turns the signal up and down over time.",
    },
    {
      name: "Rate",
      description:
        "The speed of the pulsing. Slow rates feel smooth and hypnotic, while fast rates can feel urgent or almost buzzing.",
    },
    {
      name: "Depth",
      description:
        "The intensity of the volume change. Lower depth is subtle, while higher depth can create strong pulsing or chopping.",
    },
    {
      name: "Wave Shape",
      description:
        "The shape of the modulation affects the feel. Smoother shapes sound gentle, while square-like shapes sound more abrupt and rhythmic.",
    },
  ];

  const controls = [
    {
      name: "Rate / Speed",
      description:
        "Controls how quickly the tremolo pulses. This strongly affects whether the feel is dreamy, rhythmic, or intense.",
    },
    {
      name: "Depth / Intensity",
      description:
        "Controls how much the volume moves up and down over time.",
    },
    {
      name: "Wave Shape",
      description:
        "Changes the contour of the modulation, from smooth sine-like pulses to sharper square-like chops.",
    },
    {
      name: "Sync / Note Division",
      description:
        "Lets the tremolo lock to song tempo and musical note values such as quarter notes, eighth notes, or triplets.",
    },
    {
      name: "Stereo Spread / Phase",
      description:
        "On stereo tremolo, controls how the movement is distributed between left and right channels.",
    },
    {
      name: "Mix",
      description:
        "Blends dry and effected sound on designs that support parallel-style control.",
    },
  ];

  const commonUses = [
    "Adding vintage pulsing character to clean guitar",
    "Creating hypnotic motion on chords, pads, and keys",
    "Building rhythmic movement into sustained sounds",
    "Making ambient textures feel alive without using delay or reverb alone",
    "Creating chopped or stutter-like effects in electronic or experimental music",
    "Adding stereo motion with auto-pan style tremolo",
  ];

  const bestPractices = [
    "Match the rate to the mood or groove of the song, especially if the tremolo is very audible.",
    "Use lower depth if you want subtle movement without obvious level pumping.",
    "Try tempo sync for precise rhythmic effects, but use free timing when you want a more organic feel.",
    "Check whether a smoother or sharper waveform better suits the source.",
    "Be careful with heavy tremolo on important lead parts if it reduces clarity too much.",
  ];

  const relatedLinks = [
    {
      title: "Phaser",
      to: "/learning/phaser",
      description:
        "Learn how phaser adds spectral movement, while tremolo changes volume over time.",
    },
    {
      title: "Delay",
      to: "/learning/delay",
      description:
        "Explore how delay adds repeated echoes, which is very different from tremolo’s amplitude pulsing.",
    },
    {
      title: "Reverb",
      to: "/learning/reverb",
      description:
        "Understand how reverb adds space while tremolo adds direct rhythmic motion.",
    },
    {
      title: "Chorus",
      to: "/learning/chorus",
      description:
        "See how chorus adds width and shimmer, while tremolo adds level-based pulsing.",
    },
  ];

  const sectionCardSx = {
    p: 2,
    borderRadius: 3,
    width: "100%",
    overflow: "hidden",
    boxSizing: "border-box",
  };

  return (
    <>
      <Seo
        title="Tremolo | Parish Audio Learning"
        description="Learn what tremolo does, how it modulates volume over time, how it differs from vibrato, and where tremolo is used in music production and guitar tones."
        canonical="https://parishaudio.com/learning/tremolo"
        schema={articleSchema}
      />

      <Box ref={topContainer} sx={{ width: "95%", maxWidth: 1100, mx: "auto", minHeight: height - 140 }}>
        <Paper
          sx={{
            mt: 3,
            mb: 4,
            p: { xs: 2, md: 3 },
            background: `linear-gradient(180deg, ${style.color1} 0%, rgba(22,32,48,0.98) 100%)`,
            borderRadius: 3,
            color: style.color4,
            boxShadow: 12,
            overflow: "hidden",
          }}
        >
          <Stack spacing={3.25}>
            <Box>
              <Chip
                label="Learning Center"
                sx={{
                  mb: 1.5,
                  color: style.color4,
                  backgroundColor: "rgba(255,255,255,0.08)",
                  border: "1px solid rgba(255,255,255,0.08)",
                }}
              />
              <Typography
                variant="h1"
                sx={{
                  fontSize: { xs: "2rem", md: "3rem" },
                  fontWeight: 800,
                  lineHeight: 1.05,
                  mb: 1.25,
                  color: style.color4,
                  maxWidth: 900,
                }}
              >
                Tremolo in Music Production: What It Is and How It Works
              </Typography>

              <Typography sx={{ color: style.color3, fontSize: { xs: "1rem", md: "1.1rem" }, lineHeight: 1.85, maxWidth: 950 }}>
                Tremolo is a modulation effect that changes volume over time. Instead of shifting
                pitch or creating a delay-based sweep, tremolo simply makes a sound pulse, throb,
                shimmer, or chop by moving its level up and down in a repeating pattern.
              </Typography>

              <Typography sx={{ color: style.color3, fontSize: { xs: "1rem", md: "1.1rem" }, lineHeight: 1.85, mt: 2, maxWidth: 950 }}>
                Because it is such a direct kind of modulation, tremolo can feel instantly musical.
                It can be subtle and expressive on clean guitar, dreamy on pads and keys, or sharp
                and rhythmic in more experimental production. It is one of the simplest modulation
                ideas, but also one of the most versatile.
              </Typography>
            </Box>

            <Grid container spacing={2} alignItems="stretch">
              {quickFacts.map((item) => (
                <Grid item xs={12} md={4} key={item.title} sx={{ display: "flex" }}>
                  <Paper sx={{ ...sectionCardSx, backgroundColor: panelBg, boxShadow: 6 }}>
                    <Stack direction="row" spacing={1.25} alignItems="flex-start">
                      <Box sx={{ flexShrink: 0 }}>{item.icon}</Box>
                      <Box>
                        <Typography sx={{ color: style.color4, fontWeight: 700, mb: 0.5 }}>{item.title}</Typography>
                        <Typography sx={{ color: style.color3, lineHeight: 1.7, fontSize: "0.97rem" }}>{item.text}</Typography>
                      </Box>
                    </Stack>
                  </Paper>
                </Grid>
              ))}
            </Grid>

            <Divider sx={{ borderColor: "rgba(255,255,255,0.08)" }} />

            <Box>
              <Typography variant="h2" sx={{ fontSize: { xs: "1.5rem", md: "2rem" }, fontWeight: 800, mb: 1.5, color: style.color4 }}>
                What is tremolo?
              </Typography>
              <Typography sx={{ color: style.color3, lineHeight: 1.85, mb: 2 }}>
                Tremolo is the repeated modulation of volume. When the effect is running, the
                signal gets louder and quieter in a regular pattern. This can sound smooth and
                wave-like or tight and choppy depending on the settings and waveform shape.
              </Typography>
              <Typography sx={{ color: style.color3, lineHeight: 1.85, mb: 2 }}>
                Tremolo is often confused with vibrato, but they are not the same. Tremolo changes
                level. Vibrato changes pitch. This distinction matters because the ear experiences
                the two effects very differently even though both create motion.
              </Typography>
              <Typography sx={{ color: style.color3, lineHeight: 1.85 }}>
                Tremolo has a strong connection to vintage guitar amplifiers, but it is just as
                useful in modern production where it can create rhythmic texture and animation on
                many kinds of sources.
              </Typography>
            </Box>

            <Box>
              <Typography variant="h2" sx={{ fontSize: { xs: "1.5rem", md: "2rem" }, fontWeight: 800, mb: 1.5, color: style.color4 }}>
                How tremolo works
              </Typography>
              <Grid container spacing={2} alignItems="stretch">
                {tremoloConcepts.map((item) => (
                  <Grid item xs={12} md={6} key={item.name} sx={{ display: "flex" }}>
                    <Paper sx={{ ...sectionCardSx, backgroundColor: darkPanel }}>
                      <Typography sx={{ color: accent, fontWeight: 700, mb: 0.75 }}>{item.name}</Typography>
                      <Typography sx={{ color: style.color3, lineHeight: 1.75 }}>{item.description}</Typography>
                    </Paper>
                  </Grid>
                ))}
              </Grid>
              <Typography sx={{ color: style.color3, lineHeight: 1.85, mt: 2 }}>
                Most tremolo effects use an LFO to control the volume movement. That LFO acts like
                a repeating shape, and the audio follows it. The result can feel smooth and flowing
                or rigid and mechanical depending on the selected shape and speed.
              </Typography>
            </Box>

            <Box>
              <Typography variant="h2" sx={{ fontSize: { xs: "1.5rem", md: "2rem" }, fontWeight: 800, mb: 1.5, color: style.color4 }}>
                Common types of tremolo
              </Typography>
              <Grid container spacing={2} alignItems="stretch">
                {tremoloTypes.map((item) => (
                  <Grid item xs={12} md={6} key={item.name} sx={{ display: "flex" }}>
                    <Paper sx={{ ...sectionCardSx, backgroundColor: panelBg, boxShadow: 4 }}>
                      <Typography sx={{ color: style.color4, fontWeight: 700, mb: 0.75 }}>{item.name}</Typography>
                      <Typography sx={{ color: style.color3, lineHeight: 1.75 }}>{item.description}</Typography>
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            </Box>

            <Box>
              <Typography variant="h2" sx={{ fontSize: { xs: "1.5rem", md: "2rem" }, fontWeight: 800, mb: 1.5, color: style.color4 }}>
                Tremolo vs vibrato
              </Typography>
              <Paper sx={{ p: 2, borderRadius: 3, backgroundColor: darkPanel, border: "1px solid rgba(255,255,255,0.06)" }}>
                <Stack spacing={1.5}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <CompareArrowsRoundedIcon sx={{ color: accent }} />
                    <Typography sx={{ color: style.color4, fontWeight: 700 }}>
                      Level movement vs pitch movement
                    </Typography>
                  </Stack>
                  <Typography sx={{ color: style.color3, lineHeight: 1.8 }}>
                    Tremolo changes amplitude, which means the volume pulses up and down. Vibrato
                    changes pitch, which means the note wobbles higher and lower. These two ideas
                    are often confused in guitar language, but in audio terms they describe
                    different effects.
                  </Typography>
                  <Typography sx={{ color: style.color3, lineHeight: 1.8 }}>
                    If you hear pulsing loudness, that is tremolo. If you hear pitch wobble, that
                    is vibrato.
                  </Typography>
                </Stack>
              </Paper>
            </Box>

            <Box>
              <Typography variant="h2" sx={{ fontSize: { xs: "1.5rem", md: "2rem" }, fontWeight: 800, mb: 1.5, color: style.color4 }}>
                Common tremolo controls
              </Typography>
              <Grid container spacing={2} alignItems="stretch">
                {controls.map((item) => (
                  <Grid item xs={12} md={6} key={item.name} sx={{ display: "flex" }}>
                    <Paper sx={{ ...sectionCardSx, backgroundColor: panelBg }}>
                      <Stack direction="row" spacing={1.1} alignItems="flex-start">
                        <TuneRoundedIcon sx={{ color: accent, mt: "2px", flexShrink: 0 }} />
                        <Box>
                          <Typography sx={{ color: style.color4, fontWeight: 700, mb: 0.5 }}>{item.name}</Typography>
                          <Typography sx={{ color: style.color3, lineHeight: 1.75 }}>{item.description}</Typography>
                        </Box>
                      </Stack>
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            </Box>

            <Box>
              <Typography variant="h2" sx={{ fontSize: { xs: "1.5rem", md: "2rem" }, fontWeight: 800, mb: 1.5, color: style.color4 }}>
                How tremolo is used in production
              </Typography>
              <Paper sx={{ p: 2.25, borderRadius: 3, backgroundColor: panelBg }}>
                <Stack spacing={1.35}>
                  {commonUses.map((item) => (
                    <Stack key={item} direction="row" spacing={1.1} alignItems="flex-start">
                      <LibraryMusicRoundedIcon sx={{ color: accent, mt: "2px", fontSize: 20, flexShrink: 0 }} />
                      <Typography sx={{ color: style.color3, lineHeight: 1.75 }}>{item}</Typography>
                    </Stack>
                  ))}
                </Stack>
              </Paper>
              <Typography sx={{ color: style.color3, lineHeight: 1.85, mt: 2 }}>
                Tremolo can be a tone-shaping effect, a rhythmic effect, or both. It can sit
                quietly in the background as subtle motion, or it can become one of the defining
                musical features of a part.
              </Typography>
            </Box>

            <Box>
              <Typography variant="h2" sx={{ fontSize: { xs: "1.5rem", md: "2rem" }, fontWeight: 800, mb: 1.5, color: style.color4 }}>
                Best practices for using tremolo
              </Typography>
              <Grid container spacing={2} alignItems="stretch">
                {bestPractices.map((item, index) => (
                  <Grid item xs={12} md={6} key={index} sx={{ display: "flex" }}>
                    <Paper sx={{ ...sectionCardSx, backgroundColor: darkPanel }}>
                      <Stack direction="row" spacing={1.1} alignItems="flex-start">
                        <TipsAndUpdatesRoundedIcon sx={{ color: accent, mt: "2px", flexShrink: 0 }} />
                        <Typography sx={{ color: style.color3, lineHeight: 1.75 }}>{item}</Typography>
                      </Stack>
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            </Box>

            <Box>
              <Typography variant="h2" sx={{ fontSize: { xs: "1.5rem", md: "2rem" }, fontWeight: 800, mb: 1.5, color: style.color4 }}>
                Why tremolo remains so popular
              </Typography>
              <Paper sx={{ p: 2, borderRadius: 3, backgroundColor: darkPanel, border: "1px solid rgba(255,255,255,0.06)" }}>
                <Stack spacing={1.35}>
                  {[
                    "It creates motion in a very direct and easy-to-hear way.",
                    "It can sound vintage, modern, subtle, hypnotic, or sharply rhythmic depending on the settings.",
                    "It works on many different sources, not just guitar.",
                    "It can add musical interest without changing pitch or adding obvious repeats.",
                  ].map((item) => (
                    <Stack key={item} direction="row" spacing={1.1} alignItems="flex-start">
                      <MusicNoteRoundedIcon sx={{ color: accent, mt: "2px", flexShrink: 0 }} />
                      <Typography sx={{ color: style.color3, lineHeight: 1.75 }}>{item}</Typography>
                    </Stack>
                  ))}
                </Stack>
              </Paper>
            </Box>

            <Divider sx={{ borderColor: "rgba(255,255,255,0.08)" }} />

            <Box>
              <Typography variant="h2" sx={{ fontSize: { xs: "1.5rem", md: "2rem" }, fontWeight: 800, mb: 1.5, color: style.color4 }}>
                Related learning topics
              </Typography>
              <Grid container spacing={2} alignItems="stretch">
                {relatedLinks.map((item) => (
                  <Grid item xs={12} md={6} key={item.title} sx={{ display: "flex" }}>
                    <Paper sx={{ ...sectionCardSx, backgroundColor: panelBg, display: "flex", flexDirection: "column", justifyContent: "space-between" }}>
                      <Box>
                        <Typography sx={{ color: style.color4, fontWeight: 700, mb: 0.75 }}>{item.title}</Typography>
                        <Typography sx={{ color: style.color3, lineHeight: 1.75, mb: 2 }}>{item.description}</Typography>
                      </Box>
                      <Box>
                        <Button
                          component={RouterLink}
                          to={item.to}
                          variant="outlined"
                          sx={{
                            color: style.color4,
                            borderColor: "rgba(255,255,255,0.18)",
                            "&:hover": {
                              borderColor: accent,
                              backgroundColor: "rgba(255,255,255,0.04)",
                            },
                          }}
                        >
                          Read More
                        </Button>
                      </Box>
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            </Box>

            <Divider sx={{ borderColor: "rgba(255,255,255,0.08)" }} />

            <Box>
              <Typography variant="h2" sx={{ fontSize: { xs: "1.5rem", md: "2rem" }, fontWeight: 800, mb: 1.5, color: style.color4 }}>
                Final thoughts
              </Typography>
              <Typography sx={{ color: style.color3, lineHeight: 1.9, mb: 2 }}>
                Tremolo is a simple idea with a lot of musical depth. By modulating level over
                time, it can create softness, drama, groove, and motion without relying on pitch
                changes or echoes.
              </Typography>
              <Typography sx={{ color: style.color3, lineHeight: 1.9 }}>
                Whether you want subtle shimmer or bold rhythmic chopping, tremolo remains one of
                the most expressive and timeless modulation tools in music production.
              </Typography>
            </Box>
          </Stack>
        </Paper>
      </Box>
    </>
  );
}